import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import type { ServiceCategoryFilter } from "@/components/pages/analytics/front-desk/components/services/mockData";
import { ServicesCategoryPills } from "@/components/pages/analytics/front-desk/components/services/ServicesCategoryPills";
import { parseServiceCategoryFilter } from "@/components/pages/analytics/front-desk/config/analyticsFdTabs";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { useAdminServicesHotels } from "../hooks/useAdminServicesHotels";
import { useAdminTenantTableExport } from "../hooks/useAdminTenantTableExport";
import { AdminServicesTopByHotelTable } from "./services/AdminServicesTopByHotelTable";

export const AdminServicesTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceSegment = parseServiceCategoryFilter(
    searchParams.get("segment"),
  );
  const { tenantId, rows, isLoading, isError, isReady } =
    useAdminServicesHotels(dateRange, serviceSegment);

  useAdminTenantTableExport({
    tab: "services",
    dateRange,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.services.selectDateRange",
          "Select a date range to view hotel services.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.services.loadError",
          "Failed to load services data.",
        )}
      </p>
    );
  }

  return (
    <section className="space-y-6">
      <ServicesCategoryPills
        value={serviceSegment}
        onChange={(value: ServiceCategoryFilter) => {
          setSearchParams((previousParams) => {
            const nextParams = new URLSearchParams(previousParams);
            nextParams.set("tab", "services");
            if (value === "all") {
              nextParams.delete("segment");
            } else {
              nextParams.set("segment", value);
            }
            return nextParams;
          });
        }}
      />
      <AdminServicesTopByHotelTable
        rows={rows}
        loading={isLoading}
        emptyMessage={t(
          "analytics.admin.services.table.empty",
          "No services data for the selected period.",
        )}
      />
    </section>
  );
};
